/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useMemo } from 'react';
import { Modal, message, Typography } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import { useIntl } from 'react-intl';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { appLog } from '~src/utils/log';
import { useProjectId } from '~src/hooks/useProjectId';

import { updateShare } from '~src/services/api/shares';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { useTenderId } from '~src/hooks/useTenderId';
import { DbShare } from '~src/types/db';

export interface ReactivateShareModalProps extends Partial<ModalProps> {
  share: DbShare[];
  onDone?: () => void;
}

export const ReactivateShareModal: React.FC<ReactivateShareModalProps> = ({
  share,
  onCancel,
  onDone,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const tenderId = useTenderId();

  const [mutateReactivate, { isLoading }] = useMutation(updateShare, {
    onSuccess: async () => {
      queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
      message.success(intl.formatMessage({ id: 'success' }));
    },
    onError: (error, variables) => {
      message.error(intl.formatMessage({ id: 'reactivate-failed' }));
      appLog('ReactivateShareModal -> onError -> variables', variables);
      appLog('ReactivateShareModal -> onError -> error', error);
    },
  });

  const confirmText = useMemo(() => {
    const count = share?.length || 0;
    if (count === 1) {
      const s = share[0];
      return intl.formatMessage(
        { id: 'reactivate-share-confirm' },
        { name: s?.share_key_title || s?.share_title || '—' }
      );
    }
    return intl.formatMessage(
      { id: 'reactivate-share-confirm-multiple' },
      { count }
    );
  }, [share, intl]);

  const handleOk = async () => {
    try {
      if (!projectId || !tenderId || !share || share.length === 0) return;

      await Promise.all(
        share.map((s) =>
          mutateReactivate({
            projectId,
            tenderId: Number(tenderId),
            shareKey: s.share_key_id,
            action: 'reactivate',
          })
        )
      );

      queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);

      // @ts-ignore
      onDone ? onDone() : onCancel && onCancel();
    } catch (err) {
      appLog('ReactivateShareModal -> handleOk -> err', err);
      message.error(intl.formatMessage({ id: 'reactivate-failed' }));
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'reactivate' })}
          icon={['far', 'repeat']}
        />
      }
      okText={intl.formatMessage({ id: 'reactivate' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ loading: isLoading }}
      onOk={handleOk}
      onCancel={onCancel}
      {...modalProps}>
      <Typography.Paragraph css={styles.text}>
        {confirmText}
      </Typography.Paragraph>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = () => ({
  text: {
    textAlign: 'center',
    margin: 0,
    fontSize: 15,
  },
});

export default ReactivateShareModal;
